<template>
  <div class="geothermal-schematic col-12 p-1 h-100 common-card">
    <div class="header" style="border: none;">
      <div class="title">
        계통도
      </div>
    </div>
    <div class="bg" style="box-shadow: none;">
      <div class="schematic-container">
        <div class="image-container">
          <img 
            :src="schematicImgSrc" 
            class="schematic-img"
            ref="imgRef"
            @load="onImgLoad"
          />
          <div class="text-overlay">
            <div
              v-for="(item, idx) in contentItems"
              :key="`${item.key}-${idx}`"
              class="data-text"
              :class="{ 'hover-active': hoveredIndex === idx }"
              :style="getTextStyle(idx)"
              @mouseenter="hoveredIndex = idx"
              @mouseleave="hoveredIndex = -1"
              @click="handleItemClick(item, idx)"
            >
              <span class="label">{{ item.label }}</span>
              <span class="value" :class="getValueClass(item.value)">{{ formatValue(item.value) }}</span>
              <span class="unit">{{ item.unit }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import geoGif from '@/assets/images/renewal/geo.gif';
import { useTocStore } from '~/store/tocStore';
import { useEquipStore } from '~/store/equipStore';
import moment from 'moment';

const tocStore = useTocStore();
const equipStore = useEquipStore();

const props = defineProps<{
  apiData?: any
}>();

const hoveredIndex = ref(-1);
const imgRef = ref<HTMLImageElement | null>(null);

// 최신 데이터 가져오기
const data = computed(() => {
  if (!props.apiData || !Array.isArray(props.apiData)) return null;
  const newSort = props.apiData.sort((a: any, b: any) => {
    if (moment(a.receivedDate) > moment(b.receivedDate)) {
      return -1;
    } else if (moment(a.receivedDate) < moment(b.receivedDate)) {
      return 1;
    } else {
      return 0;
    }
  });
  
  return newSort[0] || null;
});

const cid = computed(() => data.value?.cid || null);

const specInfo = ref<any>(null);

const engSource = computed(() => {
  if (!cid.value) return '';
  const cidStr = String(cid.value);
  const rIdx = cidStr.indexOf('r');
  if (rIdx !== -1 && cidStr.length > rIdx + 2) {
    return cidStr.substr(rIdx + 1, 2);
  } else {
    return cidStr.substr(0, 2);
  }
});

// 지열 계통도 이미지
const schematicImgSrc = computed(() => {
  const cid = equipStore.selectEquipInfo?.cid || '';
  if (!cid) return '';
  // 지열(30)은 geo.gif 사용
  return geoGif;
});

// 지열 데이터 정의 (새로운 폼에 맞게 재구성)
const schematicContentMap = computed(() => {
  return {
    '30': [
      { label: '축열조 급탕온도:', key: 'htank_hwatersupply_temp', unit: '°C', position: { left: '32%', top: '15%' } },
      { label: '축열조 급수온도:', key: 'htank_watersupply_temp', unit: '°C', position: { left: '58%', top: '15%' } },
      { label: '냉난방출구온도:', key: 'hp_hc_exit_temp', unit: '°C', position: { left: '40%', top: '30%' } },
      { label: '냉난방입구온도:', key: 'hp_hc_ent_temp', unit: '°C', position: { left: '40%', top: '55%' } },
      { label: '지열수입구온도:', key: 'hp_geowater_ent_temp', unit: '°C', position: { left: '15%', top: '40%' } },
      { label: '지열수출구온도:', key: 'hp_getwater_exit_temp', unit: '°C', position: { left: '30%', top: '60%' } },
      { label: '부하측출구온도:', key: 'load_exit_temp', unit: '°C', position: { left: '60%', top: '65%' } },
      { label: '부하측입구온도:', key: 'load_ent_temp', unit: '°C', position: { left: '60%', top: '35%' } },
    ]
  };
});

// 데이터와 라벨 매칭
const contentItems = computed(() => {
  const eng = engSource.value;
  const contentMap = schematicContentMap.value[eng as keyof typeof schematicContentMap.value] || [];
  const rawData = data.value || {};
  const items = contentMap.map((item: any) => ({
    ...item,
    value: rawData[item.key] !== undefined && rawData[item.key] !== null ? rawData[item.key] : null
  }));
  
  return items;
});

// 텍스트 스타일 계산
function getTextStyle(idx: number): Record<string, string> {
  const eng = engSource.value;
  const contentMap = schematicContentMap.value[eng as keyof typeof schematicContentMap.value] || [];
  const item = contentMap[idx];
  
  if (item && item.position) {
    return {
      position: 'absolute',
      left: item.position.left,
      top: item.position.top,
      fontSize: '13px',
      fontWeight: 'bold',
      color: '#000',
      zIndex: '10',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      padding: '4px 8px',
      borderRadius: '4px',
      backgroundColor: hoveredIndex.value === idx ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)',
      boxShadow: hoveredIndex.value === idx ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
    };
  }
  
  return {
    position: 'absolute',
    left: '10%',
    top: '10%',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#000',
    zIndex: '10'
  };
}

// 값 포맷팅
function formatValue(value: any): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') {
    return value.toFixed(1);
  }
  return String(value);
}

// 값에 따른 클래스 반환 (온도 범위에 따라 색상 변경)
function getValueClass(value: any): string {
  if (value === null || value === undefined) return 'value-null';
  if (typeof value === 'number') {
    if (value < 0) return 'value-low';
    if (value > 50) return 'value-high';
    return 'value-normal';
  }
  return 'value-normal';
}

// 아이템 클릭 핸들러
function handleItemClick(item: any, idx: number) {
  console.log('Item clicked:', item);
  // 필요시 상세 정보 모달 표시 또는 페이지 이동
  // 예: Swal.fire 또는 router.push
}

function onImgLoad() {
  nextTick(() => {
    if (imgRef.value) {
      console.log('Geothermal schematic image loaded');
    }
  });
}

// specInfo 비동기 로드
watch(cid, async (newCid) => {
  if (newCid) {
    try {
      specInfo.value = await equipStore.getEquipSpecInfo(newCid) as any;
    } catch (error) {
      console.error('Failed to load spec info:', error);
      specInfo.value = null;
    }
  } else {
    specInfo.value = null;
  }
}, { immediate: true });

onMounted(() => {
  const handleResize = () => {
    // 리사이즈 시 처리 (필요시)
  };
  
  window.addEventListener('resize', handleResize);
  
  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
  });
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/design-toc/schematic-card.scss";
@import "@/assets/styles/design-toc/define_font.scss";

.geothermal-schematic {
  .schematic-container {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  .image-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .schematic-img {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .text-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .data-text {
    pointer-events: auto;
    font-family: Arial, sans-serif;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;

    .label {
      @include text-bold-12;
      color: #333;
    }

    .value {
      @include text-bold-14;
      font-weight: 700;
      min-width: 50px;
      text-align: right;

      &.value-normal {
        color: #0687f1;
      }

      &.value-high {
        color: #F56B21;
      }

      &.value-low {
        color: #42A5F5;
      }

      &.value-null {
        color: #999;
      }
    }

    .unit {
      @include text-normal-12;
      color: #666;
    }

    &.hover-active {
      transform: scale(1.05);
      z-index: 20 !important;
    }
  }

  // 반응형 미디어 쿼리
  @media (max-width: 768px) {
    .data-text {
      font-size: 11px !important;
      padding: 2px 4px !important;
    }
  }

  @media (max-width: 480px) {
    .data-text {
      font-size: 10px !important;
      padding: 2px 4px !important;
    }
  }
}
</style>

